import { ReactNode, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AppScope, appScopeUtils } from '../appScope.types';
import { AppScopeProvider } from './AppScopeProvider';
import { AppScopeContext } from './AppScopeContext';
import { isInitializedAppScope } from './internalAppScope';

type UiState = {
  appScope: AppScope | null;
};

/**
 * ModalHandler renders outside route AppScopeProvider.
 * Stash on ui.appScope is the only way workspace/team (or public org) reach the
 * portal. Legacy openModal with no stash is organization-level from the root
 * GlobalAppScopeProvider — never nested browsing scope, never inferred from URL.
 */
export function PortaledModalAppScope({ children }: { children: ReactNode }) {
  const contextScope = useContext(AppScopeContext);
  const stashedAppScope = useSelector(
    (state: { ui: UiState }) => state.ui.appScope
  );

  const scope = useMemo((): AppScope => {
    if (stashedAppScope) {
      appScopeUtils.assertValid(stashedAppScope);
      return stashedAppScope;
    }
    if (contextScope && isInitializedAppScope(contextScope)) {
      return {
        level: 'organization',
        organizationId: contextScope.organizationId
      };
    }
    throw new Error(
      'AppScope is not initialized yet — consumer mounted before organizationId was available'
    );
  }, [stashedAppScope, contextScope]);

  return <AppScopeProvider scope={scope}>{children}</AppScopeProvider>;
}
